/**
 * Soulmate Sketch report — the render contract shared with the backend
 * (see soulmate-report-spec.md). Enums are lowercase; the UI derives the three
 * dimension sign pairs from `you`/`soulmate` (Trust→sun, Love→moon, Emotion→ruling).
 */

export type Zodiac =
  | "aries"
  | "taurus"
  | "gemini"
  | "cancer"
  | "leo"
  | "virgo"
  | "libra"
  | "scorpio"
  | "sagittarius"
  | "capricorn"
  | "aquarius"
  | "pisces"

export type Planet =
  | "sun"
  | "moon"
  | "mercury"
  | "venus"
  | "mars"
  | "jupiter"
  | "saturn"
  | "uranus"
  | "neptune"
  | "pluto"

export type DimensionLevel = "low" | "medium" | "high"

export interface SoulmateDimension {
  percent: number
  level: DimensionLevel
  text: string
}

export interface SoulmatePortrait {
  assetUrl: string
  gender: "male" | "female"
  ethnicity: "african" | "caucasian" | "hispanic" | "asian"
}

export interface SoulmateChart {
  sunSign: Zodiac
  moonSign: Zodiac
  rulingPlanet: Planet
}

export interface SoulmateReport {
  /** Set by the backend (portrait selector), not the LLM. */
  portrait: SoulmatePortrait
  soulmate: { suggestedName: string } & SoulmateChart
  you: SoulmateChart
  /** Overall orb, int 0..100. */
  compatibilityScore: number
  intro: { matchDescription: string }
  firstImpression: string
  whyThisMatchWorks: string
  theirLoveEnergy: string
  youInThisConnection: string
  meeting: {
    intro: string
    where: [string, string, string, string]
    when: [string, string, string, string]
  }
  dimensions: {
    trust: SoulmateDimension
    love: SoulmateDimension
    emotion: SoulmateDimension
  }
  relationshipSnapshot: string
  validation: string
}

export function isSoulmateReport(content: unknown): content is SoulmateReport {
  return Boolean(content && typeof content === "object" && "portrait" in content)
}

/**
 * Request snapshot for the public soulmate report (built from raw quiz answers).
 * Partner fields exist only for the soulmate quiz; other quizzes send just
 * `gender` + `dob` (partner fields null).
 */
export interface SoulmateInput {
  gender: "male" | "female" | "other"
  partnerGender?: "male" | "female" | "any" | null
  partnerEthnicity?: "african" | "caucasian" | "hispanic" | "asian" | "any" | null
  partnerAge?: string | null
  dob: string
  preferences?: Record<string, string | string[]>
}
